/**
 * CLIENTS PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass cards with staggered animations
 * - StatCard components for metrics
 * - Glass modal forms with enhanced inputs
 * - Skeleton loading states
 * - Filter bar with glass styling
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Mail,
  Building2,
  Loader2,
  AlertCircle,
  RefreshCw,
  CreditCard,
  Users,
  DollarSign,
  ChevronRight
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input, SearchInput, Textarea } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { SkeletonStatCard, SkeletonCard, Skeleton } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'
import { cn } from '../../lib/utils'

interface Client {
  id: number
  name: string
  company: string | null
  email: string | null
  payment_method: 'Wise' | 'Remitly' | 'Local Bank' | 'Other'
  status: 'Active' | 'Inactive'
  notes: string | null
  created_at: string
  updated_at: string
}

// Spring animation config
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

// Staggered animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

export function ClientsPage() {
  const [loading, setLoading] = useState(true)
  const [clients, setClients] = useState<Client[]>([])
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [paymentFilter, setPaymentFilter] = useState('all')

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    payment_method: 'Wise' as 'Wise' | 'Remitly' | 'Local Bank' | 'Other',
    status: 'Active' as 'Active' | 'Inactive',
    notes: ''
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchClients()
  }, [])

  const fetchClients = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/clients')
      if (response.success) {
        setClients(response.data || [])
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err)
      setError('Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}
    if (!formData.name.trim()) errors.name = 'Name is required'
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAdd = async () => {
    if (!validateForm()) return

    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.post('/clients', formData)
      if (response.success) {
        await fetchClients()
        setIsAddModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to add client:', err)
      setError(err.message || 'Failed to add client')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async () => {
    if (!validateForm() || !selectedClient) return

    try {
      setSaving(true)
      setError(null)
      const response = await apiClient.put(`/clients/${selectedClient.id}`, formData)
      if (response.success) {
        await fetchClients()
        setIsEditModalOpen(false)
        resetForm()
      }
    } catch (err: any) {
      console.error('Failed to update client:', err)
      setError(err.message || 'Failed to update client')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedClient) return

    try {
      setDeleting(true)
      const response = await apiClient.delete(`/clients/${selectedClient.id}`)
      if (response.success) {
        await fetchClients()
        setIsDeleteDialogOpen(false)
        setSelectedClient(null)
      }
    } catch (err: any) {
      console.error('Failed to delete client:', err)
      setError(err.message || 'Failed to delete client. It may have existing transactions.')
    } finally {
      setDeleting(false)
    }
  }

  const openEditModal = (client: Client) => {
    setSelectedClient(client)
    setFormData({
      name: client.name,
      company: client.company || '',
      email: client.email || '',
      payment_method: client.payment_method,
      status: client.status,
      notes: client.notes || ''
    })
    setIsEditModalOpen(true)
  }

  const openViewModal = (client: Client) => {
    setSelectedClient(client)
    setIsViewModalOpen(true)
  }

  const openDeleteDialog = (client: Client) => {
    setSelectedClient(client)
    setIsDeleteDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      payment_method: 'Wise',
      status: 'Active',
      notes: ''
    })
    setFormErrors({})
    setSelectedClient(null)
  }

  // Filter clients
  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.company?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (client.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter
    const matchesPayment = paymentFilter === 'all' || client.payment_method === paymentFilter
    return matchesSearch && matchesStatus && matchesPayment
  })

  // Stats
  const totalClients = clients.length
  const activeClients = clients.filter(c => c.status === 'Active').length
  const wiseClients = clients.filter(c => c.payment_method === 'Wise').length

  const getPaymentMethodBadge = (method: string) => {
    switch (method) {
      case 'Wise': return 'blue'
      case 'Remitly': return 'purple'
      case 'Local Bank': return 'green'
      default: return 'default'
    }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Skeleton width={100} height={32} rounded="lg" className="mb-2" />
            <Skeleton width={320} height={20} rounded="md" />
          </div>
          <div className="flex gap-3">
            <Skeleton width={100} height={44} rounded="xl" />
            <Skeleton width={120} height={44} rounded="xl" />
          </div>
        </div>

        {/* Stats skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-8">
          {[...Array(3)].map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>

        {/* Filter skeleton */}
        <GlassCard hoverable={false} className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Skeleton height={44} rounded="xl" className="md:col-span-2" />
            <Skeleton height={44} rounded="xl" />
            <Skeleton height={44} rounded="xl" />
          </div>
        </GlassCard>

        {/* Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} showAvatar lines={2} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary mb-1.5 tracking-tight">
            Clients
          </h1>
          <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
            Manage companies and individuals who pay to the partnership
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={fetchClients}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Client
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6"
          >
            <GlassCard variant="regular" hoverable={false} className="border border-red/20 bg-red-glass">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red dark:text-red-dark" />
                <p className="text-red dark:text-red-dark text-subheadline font-medium">{error}</p>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={3}>
          <StatCard
            title="Total Clients"
            value={totalClients.toString()}
            icon={<Users className="w-6 h-6" strokeWidth={2} />}
            color="purple"
          />
          <StatCard
            title="Active Clients"
            value={activeClients.toString()}
            icon={<DollarSign className="w-6 h-6" strokeWidth={2} />}
            color="green"
          />
          <StatCard
            title="Wise Payments"
            value={wiseClients.toString()}
            icon={<CreditCard className="w-6 h-6" strokeWidth={2} />}
            color="blue"
          />
        </StatCardGrid>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="mt-8 mb-6">
        <GlassCard variant="regular" hoverable={false}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <SearchInput
                placeholder="Search by name, company, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' }
              ]}
            />
            <Select
              value={paymentFilter}
              onChange={setPaymentFilter}
              options={[
                { value: 'all', label: 'All Payment Methods' },
                { value: 'Wise', label: 'Wise' },
                { value: 'Remitly', label: 'Remitly' },
                { value: 'Local Bank', label: 'Local Bank' },
                { value: 'Other', label: 'Other' }
              ]}
            />
          </div>
        </GlassCard>
      </motion.div>

      {/* Clients Grid */}
      <motion.div variants={itemVariants}>
        {filteredClients.length === 0 ? (
          <GlassCard variant="regular" hoverable={false} className="py-16">
            <div className="flex flex-col items-center justify-center text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple via-blue to-pink flex items-center justify-center mb-5 shadow-glass-lg"
              >
                <Users className="w-10 h-10 text-white" strokeWidth={1.5} />
              </motion.div>
              <h3 className="text-title3 font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No Clients Found
              </h3>
              <p className="text-body text-light-text-secondary dark:text-dark-text-secondary mb-6 max-w-sm">
                {searchQuery || statusFilter !== 'all' || paymentFilter !== 'all'
                  ? 'No clients match your filters'
                  : 'Add your first client to get started'}
              </p>
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setIsAddModalOpen(true)}
              >
                Add Client
              </Button>
            </div>
          </GlassCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {filteredClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, ...springConfig }}
              >
                <GlassCard
                  variant="regular"
                  hoverable
                  pressable
                  className="group"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <motion.div
                        className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-glass-sm dark:shadow-dark-glass-sm flex-shrink-0"
                        whileHover={{ scale: 1.08, rotate: 3 }}
                        transition={springConfig}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-purple via-blue to-pink animate-liquid-shimmer bg-[length:200%_200%]" />
                        <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent" />
                        <div className="relative w-full h-full flex items-center justify-center">
                          <span className="text-white font-bold text-subheadline tracking-tight drop-shadow-sm">
                            {client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      </motion.div>
                      <div className="min-w-0">
                        <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary truncate group-hover:text-blue dark:group-hover:text-blue-dark transition-colors">
                          {client.name}
                        </h3>
                        {client.company && (
                          <p className="text-caption1 text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-1 truncate">
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            {client.company}
                          </p>
                        )}
                      </div>
                    </div>
                    <StatusBadge
                      status={client.status === 'Active' ? 'success' : 'inactive'}
                      label={client.status}
                      size="sm"
                    />
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    {client.email && (
                      <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary flex items-center gap-2 truncate">
                        <Mail className="w-4 h-4 text-light-text-quaternary flex-shrink-0" />
                        {client.email}
                      </p>
                    )}
                    <div>
                      <Badge
                        variant={getPaymentMethodBadge(client.payment_method) as any}
                        size="sm"
                        icon={<CreditCard className="w-3 h-3" />}
                      >
                        {client.payment_method}
                      </Badge>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-light-separator dark:border-dark-separator">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        openViewModal(client)
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-2.5 px-3 rounded-xl liquid-material-clear hover:bg-blue-glass text-blue dark:text-blue-dark transition-all text-subheadline font-semibold flex items-center justify-center gap-1.5"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        openEditModal(client)
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 py-2.5 px-3 rounded-xl liquid-material-clear hover:bg-orange-glass text-orange dark:text-orange-dark transition-all text-subheadline font-semibold flex items-center justify-center gap-1.5"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation()
                        openDeleteDialog(client)
                      }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-2.5 rounded-xl liquid-material-clear hover:bg-red-glass text-red dark:text-red-dark transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Add Client Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false)
          resetForm()
        }}
        title="Add New Client"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }} className="space-y-5">
          <Input
            label="Client Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="John Doe or Company Inc."
            required
            error={formErrors.name}
          />

          <Input
            label="Company (Optional)"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Company name"
            icon={<Building2 className="w-4 h-4" />}
          />

          <Input
            label="Email (Optional)"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="client@example.com"
            icon={<Mail className="w-4 h-4" />}
          />

          <Select
            label="Payment Method"
            value={formData.payment_method}
            onChange={(value) => setFormData({ ...formData, payment_method: value as any })}
            options={[
              { value: 'Wise', label: 'Wise' },
              { value: 'Remitly', label: 'Remitly' },
              { value: 'Local Bank', label: 'Local Bank' },
              { value: 'Other', label: 'Other' }
            ]}
          />

          <Select
            label="Status"
            value={formData.status}
            onChange={(value) => setFormData({ ...formData, status: value as any })}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
          />

          <Textarea
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Any additional notes about this client..."
            rows={3}
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsAddModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={saving}
            >
              Add Client
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Client Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          resetForm()
        }}
        title="Edit Client"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleEdit(); }} className="space-y-5">
          <Input
            label="Client Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="John Doe or Company Inc."
            required
            error={formErrors.name}
          />

          <Input
            label="Company (Optional)"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            placeholder="Company name"
            icon={<Building2 className="w-4 h-4" />}
          />

          <Input
            label="Email (Optional)"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="client@example.com"
            icon={<Mail className="w-4 h-4" />}
          />

          <Select
            label="Payment Method"
            value={formData.payment_method}
            onChange={(value) => setFormData({ ...formData, payment_method: value as any })}
            options={[
              { value: 'Wise', label: 'Wise' },
              { value: 'Remitly', label: 'Remitly' },
              { value: 'Local Bank', label: 'Local Bank' },
              { value: 'Other', label: 'Other' }
            ]}
          />

          <Select
            label="Status"
            value={formData.status}
            onChange={(value) => setFormData({ ...formData, status: value as any })}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
          />

          <Textarea
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                setIsEditModalOpen(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={saving}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Client Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false)
          setSelectedClient(null)
        }}
        title="Client Details"
        size="md"
      >
        {selectedClient && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 pb-6 border-b border-light-separator dark:border-dark-separator">
              <motion.div
                className="relative w-16 h-16 rounded-2xl overflow-hidden shadow-glass dark:shadow-dark-glass"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={springConfig}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple via-blue to-pink animate-liquid-shimmer bg-[length:200%_200%]" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <span className="text-white font-bold text-title2 tracking-tight drop-shadow-sm">
                    {selectedClient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
              </motion.div>
              <div>
                <h3 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary">
                  {selectedClient.name}
                </h3>
                {selectedClient.company && (
                  <p className="text-body text-light-text-secondary dark:text-dark-text-secondary">
                    {selectedClient.company}
                  </p>
                )}
                <div className="mt-2">
                  <StatusBadge
                    status={selectedClient.status === 'Active' ? 'success' : 'inactive'}
                    label={selectedClient.status}
                  />
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Email</p>
                <p className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary">
                  {selectedClient.email || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Payment Method</p>
                <Badge
                  variant={getPaymentMethodBadge(selectedClient.payment_method) as any}
                  icon={<CreditCard className="w-3 h-3" />}
                >
                  {selectedClient.payment_method}
                </Badge>
              </div>
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Added</p>
                <p className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary">
                  {new Date(selectedClient.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Last Updated</p>
                <p className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary">
                  {new Date(selectedClient.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {selectedClient.notes && (
              <div>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Notes</p>
                <p className="text-body text-light-text-primary dark:text-dark-text-primary liquid-material-clear p-4 rounded-xl">
                  {selectedClient.notes}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setIsViewModalOpen(false)
                  openEditModal(selectedClient)
                }}
                icon={<Edit className="w-4 h-4" />}
              >
                Edit
              </Button>
              <Button
                variant="primary"
                fullWidth
                onClick={() => setIsViewModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedClient(null)
        }}
        onConfirm={handleDelete}
        title="Delete Client"
        message={`Are you sure you want to delete ${selectedClient?.name}? This action cannot be undone. Clients with existing transactions cannot be deleted.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
    </motion.div>
  )
}
